<script setup lang="ts">

interface Props {
  file: UploadMetaData,
  showGoToButton?: boolean,
}

const fileUploadStore = useFileUploadStore();

const props = withDefaults(defineProps<Props>(), {
  showGoToButton: false
});

const emits = defineEmits<{
  'retry-file': [file: UploadMetaData],
  'goto': [file: UploadMetaData],
}>();

const isImage = computed(() => props.file.fileMimeType.split('/')[0] === 'image');
const progressInfo = computed(() => fileUploadStore.progressMap[props.file.fileName]);
const progressVal = computed(() => progressInfo.value?.ratio ?? 0);
const progressTotal = computed(() => progressInfo.value?.total ?? 0);

function isProjectUpload(file: UploadMetaData): file is ProjectUploadMetadata {
  return 'uploadType' in file && file.uploadType === 'project';
}

function isQAUpload(file: UploadMetaData): file is QAUploadMetadata {
  return 'uploadType' in file && file.uploadType === 'qa';
}

</script>

<template>
  <IonItem lines="none">
    <IonThumbnail slot="start">
      <IonImg
        v-if="file.imageData && isImage"
        :src="file.imageData"
      />
      <IonIcon
        v-else
        class="icon"
        color="medium"
        :icon="ioniconsDocument"
      />
    </IonThumbnail>
    <IonCol>
      <IonRow>{{ decodeURI(file.fileName) }}</IonRow>
      <IonRow>
        <DocumentsItemStatus
          :status="file.status"
          :progress="progressVal"
          :total-bytes="progressTotal"
        />
      </IonRow>
    </IonCol>

    <template v-if="showGoToButton && file.projectId !== undefined && file.worksheetId !== null">
      <IonButton
        v-if="isProjectUpload(file)"
        slot="end"
        fill="clear"
        @click="emits('goto', file)"
      >
        <IonIcon
          slot="icon-only"
          style="font-size: 2rem"
          class="fa-fw fa-light fa-clipboard"
        />
      </IonButton>
      <IonButton
        v-else-if="isQAUpload"
        slot="end"
        fill="clear"
        @click="emits('goto', file)"
      >
        <IonIcon
          slot="icon-only"
          style="font-size: 2rem"
          class="fa-fw fa-light fa-clipboard-list-check"
        />
      </IonButton>
    </template>
    <IonButton
      v-if="file.status === 'failed'"
      slot="end"
      fill="clear"
      @click="emits('retry-file', file)"
    >
      <IonIcon
        slot="icon-only"
        style="font-size: 2rem"
        :icon="ioniconsRefreshCircle"
      />
    </IonButton>
  </IonItem>
</template>

<style scoped>
ion-thumbnail {
    --border-radius: 8px;
    border: 1px solid var(--ion-color-step-200);
    display: flex;
    justify-content: center;
    align-items: center;
}

.icon {
    font-size: 1.8rem;
}
</style>
